import { EventType } from "@/analytics/analytics-events";
import {
  CreateAnnotationShareLinkEventProperties,
  EditAnnotationEventProperties,
  GotoAnnotationProperties,
  OpenAnnotationAttachmentEventProperties,
  OpenAnnotationDetailsContextMenuEventProperties,
} from "@/analytics/annotation-events-properties";
import { AuthorizeExternalAnnotationProperties } from "@/analytics/generic-events-properties";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { MeasurementValuesField } from "@/components/r3f/renderers/annotations/annotation-renderers/measurement-values-field";
import { HandleAnnotationViewerVariantReturn } from "@/components/r3f/renderers/annotations/annotation-renderers/use-handle-annotation-details-variant";
import { EditAnnotation } from "@/components/ui/annotations/edit-annotation";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAnnotationAssignees } from "@/hooks/use-annotation-assignees";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { useGoToAnnotation } from "@/hooks/use-go-to-annotation";
import { useShareAnnotationOrAnalysis } from "@/hooks/use-share-annotation-or-analysis";
import { selectIsElementAligned } from "@/modes/walk-mode/walk-scene-selectors";
import { selectBackgroundTaskForIElement } from "@/store/background-tasks/background-tasks-selector";
import {
  selectProjectIdForIntegrationType,
  selectWorkspaceIntegrations,
} from "@/store/integrations/integrations-selectors";
import {
  selectProjectUser,
  selectProjectUsers,
} from "@/store/project-selector";
import { selectMarkupAttachments } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { MutationTaskType } from "@/utils/background-tasks";
import { useDashboardProjectIntegrationsUrl } from "@/utils/redirects";
import {
  getAnnotationStatusDisplayName,
  selectIElementWorldPosition,
} from "@faro-lotv/app-component-toolbox";
import { FileDetails, grey, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElementType,
  isIElementAttachment,
  isIElementExternalMarkup,
  isIElementMarkup,
  isIElementMarkupDueTime,
  isIElementMarkupState,
  isIElementMeasurePolygon,
  isIElementUrlLink,
  isValid,
  MarkupIElement,
} from "@faro-lotv/ielement-types";
import {
  createSignedUrlForIElementThumbnailUri,
  createSignedUrlForIElementUri,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectCompanyId,
  selectIElement,
  selectProjectId,
  useKeepSignedUrlsAlive,
  useValidSignedUrls,
} from "@faro-lotv/project-source";
import {
  ApiResponseError,
  BcfBackendApiErrorDetailsType,
  BcfServicesIntegrationType,
  GetTopicDocumentReference,
  GetTopicResponse,
  isBcfBackendApiErrorDetails,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Box, Dialog, Stack, SxProps, Theme } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  formatSparseCoordinateString,
  getCoordinateStringZUp,
} from "../scene-context-menu";
import { isTagReadable } from "../tags/tag-accessibility";
import { removeDeepLinkFromDescription } from "./annotation-props";
import { AnnotationViewer } from "./annotation-viewer/annotation-viewer";
import {
  ExternalAnnotationsField,
  UrlLink,
} from "./annotation-viewer/annotation-viewer-types";
import { ExternalAnnotationProviderLogo } from "./external-annotation-provider-logo";
import {
  ExternalAnnotationError,
  ExternalAnnotationErrorType,
} from "./external-annotation-types";
import {
  EXTERNAL_ANNOTATION_ERROR,
  getBcfIntegrationTypeForIElementType,
  getExternalAnnotationProviderName,
  getExternalAnnotationTypeName,
  getGenericUserInfo,
  getWorkspaceIntegrationProviderId,
  isDocumentApiImplemented,
  KNOWN_BCF_BACKEND_CORE_API_ERROR_CODES,
} from "./external-annotation-utils";
import { ProviderAuthorization } from "./provider-authorization/provider-authorization";

interface MarkupAnnotationUIProps extends Omit<
  HandleAnnotationViewerVariantReturn,
  "onAnnotationViewerHovered"
> {
  /** The details about a specific annotation */
  markup: MarkupIElement;

  /** Optional icon to render next to the UI. Used for single point annotations */
  icon?: JSX.Element;

  /** Pixel size to use for the icon */
  iconSize?: number;

  /** Optional size to use for the annotation (in px) */
  annotationSize?: { minWidth: number; width: number; height: number };

  /** Optional style to apply to the Markup's UI */
  sx?: SxProps<Theme>;

  /** Callback executed when the copy to clipboard button is pressed */
  onCopyToClipboard?(): void;

  /** Callback executed when the user requests to delete the annotation. */
  onDelete(): void;
}

/** @returns the ui to interact with an annotation */
export function MarkupAnnotationUI({
  markup,
  icon,
  iconSize,
  annotationSize,
  sx,
  AnnotationViewerVariant,
  onAnnotationViewerExpanded,
  onAnnotationViewerClosed,
  onAnnotationViewerClicked,
  onCopyToClipboard,
  onDelete,
}: MarkupAnnotationUIProps): JSX.Element | null {
  const [externalMarkupData, setExternalMarkupData] =
    useState<GetTopicResponse>();
  const [externalMarkupLinks, setExternalMarkupLinks] = useState<
    UrlLink[] | undefined
  >();

  const [externalAnnotationError, setExternalAnnotationError] =
    useState<ExternalAnnotationError>();
  const { openToast } = useToast();

  const externalMarkup = useAppSelector(
    selectChildDepthFirst(markup, isIElementExternalMarkup),
  );

  const { bcfServicesApiClient, projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const integrationType = externalMarkup
    ? getBcfIntegrationTypeForIElementType(externalMarkup.type)
    : undefined;

  const integrationProjectId = useAppSelector(
    integrationType
      ? selectProjectIdForIntegrationType(integrationType)
      : () => undefined,
  );

  const workspaceIntegrations = useAppSelector(selectWorkspaceIntegrations);
  const projectId = useAppSelector(selectProjectId);
  const companyId = useAppSelector(selectCompanyId);
  const dashboardProjectIntegrationsUrl = useDashboardProjectIntegrationsUrl(
    companyId,
    projectId,
  );

  useEffect(() => {
    async function fetchExternalMarkupData(): Promise<void> {
      setExternalMarkupData(undefined);
      try {
        assert(integrationType, "Integration type is not defined");
        assert(
          externalMarkup?.externalIssueId,
          "External issue ID is not defined",
        );
        assert(integrationProjectId, "Integration project ID is not defined");
        const topic = await bcfServicesApiClient.getTopic(
          integrationType,
          integrationProjectId,
          externalMarkup.externalIssueId,
        );
        setExternalMarkupData(topic);
        if (isDocumentApiImplemented(integrationType)) {
          const documentReferences =
            await bcfServicesApiClient.getTopicDocumentReferences(
              integrationType,
              integrationProjectId,
              externalMarkup.externalIssueId,
            );
          const documentReferencesLink: UrlLink[] | undefined =
            documentReferences.length === 0
              ? undefined
              : documentReferences.map((ref: GetTopicDocumentReference) => ({
                  href: ref.url ?? "",
                  label: ref.description ?? ref.url ?? "",
                }));
          setExternalMarkupLinks(documentReferencesLink);
        }
      } catch (error) {
        if (error instanceof ApiResponseError) {
          if (isBcfBackendApiErrorDetails(error.body)) {
            let errorType: ExternalAnnotationErrorType = "connectionError";
            let errorMessage: string | undefined;
            switch (error.body.title) {
              case BcfBackendApiErrorDetailsType.coreApiError: {
                if (
                  KNOWN_BCF_BACKEND_CORE_API_ERROR_CODES.find(
                    (errorCode) => errorCode === error.body.status,
                  )
                ) {
                  errorType = "sessionMightHaveExpired";
                  errorMessage =
                    "Your session with " +
                    `${integrationType ? getExternalAnnotationProviderName(integrationType) : "external annotation provider"} ` +
                    "might have expired. To continue, please refresh the connection by clicking the Authorize button.";
                }
                break;
              }
              case BcfBackendApiErrorDetailsType.autodeskApiError:
              case BcfBackendApiErrorDetailsType.procoreApiError: {
                errorMessage = error.body.detail;
                break;
              }
              default:
                break;
            }
            if (errorMessage) {
              return setExternalAnnotationError({
                type: errorType,
                message: errorMessage,
                ...EXTERNAL_ANNOTATION_ERROR[errorType],
              });
            }
          }
          // Handle the other cases where the error is not related to the BCF Backend API
          handleErrorWithToast({
            title: "Error fetching external annotation data",
            error,
          });
        }
      }
    }

    if (externalMarkup && integrationType) {
      const workspaceIntegrationEnabled = workspaceIntegrations.includes(
        getWorkspaceIntegrationProviderId(integrationType),
      );
      const projectIntegrationEnabled = integrationProjectId !== undefined;
      if (workspaceIntegrationEnabled && projectIntegrationEnabled) {
        // Reset error state in case there was a previous error that was resolved now
        setExternalAnnotationError(undefined);
        fetchExternalMarkupData();
      } else if (projectIntegrationEnabled === false) {
        setExternalAnnotationError({
          type: "projectIsDisconnected",
          message:
            `Please connect to one ${getExternalAnnotationProviderName(integrationType)} project through the Sphere XG project's ` +
            `[integration settings](${dashboardProjectIntegrationsUrl}) and refresh this page once done.`,
          ...EXTERNAL_ANNOTATION_ERROR.projectIsDisconnected,
        });
      } else {
        setExternalAnnotationError({
          type: "userTokenMissing",
          message:
            `You need to login to your ${getExternalAnnotationProviderName(integrationType)} account ` +
            "and authorize Sphere XG to access your annotations data. " +
            "Please click on the Authorize button and follow the instructions.",
          ...EXTERNAL_ANNOTATION_ERROR.userTokenMissing,
        });
      }
    }
  }, [
    bcfServicesApiClient,
    dashboardProjectIntegrationsUrl,
    externalMarkup,
    handleErrorWithToast,
    integrationProjectId,
    integrationType,
    workspaceIntegrations,
  ]);

  const sphereMarkup = useAppSelector(
    selectChildDepthFirst(markup, isIElementMarkup),
  );

  // Check if the markup has an attachment
  const hasAttachment = useAppSelector(
    (state) => selectMarkupAttachments(markup)(state).length > 0,
  );

  // Collect the info related to this annotation
  const dueDate = useAppSelector(
    selectChildDepthFirst(sphereMarkup, isIElementMarkupDueTime),
  );
  const state = useAppSelector(
    selectChildDepthFirst(sphereMarkup, isIElementMarkupState),
  );
  const mainAssignee = useAnnotationAssignees(sphereMarkup).at(0);
  const projectUsers = useAppSelector(selectProjectUsers);

  // The creator of the annotation
  const projectUser = useAppSelector(
    selectProjectUser(sphereMarkup?.createdBy),
  );
  const currentUser = useAppSelector(selectCurrentUser);
  const createdBy =
    currentUser?.id === sphereMarkup?.createdBy ? currentUser : projectUser;

  // For now use the first assignee
  const assigneeId = useMemo(
    () =>
      mainAssignee
        ? projectUsers.find((user) => user.id === mainAssignee.id)?.id
        : undefined,
    [mainAssignee, projectUsers],
  );

  const { canWriteAnnotations } = useAnnotationPermissions();
  const [isEditing, setIsEditing] = useState(false);

  const shareAnnotation = useShareAnnotationOrAnalysis(markup.id, true);

  const measurement = useAppSelector(
    selectAncestor(sphereMarkup, isIElementMeasurePolygon),
  );
  const isMeasurement = !!measurement;

  const attachmentsElements = useAppSelector(
    selectChildrenDepthFirst(sphereMarkup, isIElementAttachment),
    isEqual,
  );

  const signedFileUrls = useMemo(
    () =>
      attachmentsElements.map((el) =>
        createSignedUrlForIElementUri(projectApiClient, el),
      ),
    [attachmentsElements, projectApiClient],
  );
  const validSignedFileUrls = useValidSignedUrls(signedFileUrls);

  const signedThumbnailUrls = useMemo(
    () =>
      attachmentsElements.map((el) =>
        createSignedUrlForIElementThumbnailUri(projectApiClient, el),
      ),
    [attachmentsElements, projectApiClient],
  );
  const validSignedThumbnailUrls = useValidSignedUrls(signedFileUrls);

  const allSignedUrls = useMemo(
    () => [
      ...signedFileUrls.filter(isValid),
      ...signedThumbnailUrls.filter(isValid),
    ],
    [signedFileUrls, signedThumbnailUrls],
  );

  const attachments = useMemo<FileDetails[]>(
    () =>
      attachmentsElements.map((el, index) => ({
        id: el.id,
        fileName: el.name,
        fileSize: el.fileSize,
        date: el.createdAt,
        isLoading: !validSignedFileUrls[index],
        urlOrFile: validSignedFileUrls[index] ?? "loading placeholder string",
        thumbnailUrl: validSignedThumbnailUrls[index] ?? el.thumbnailUri,
      })),
    [attachmentsElements, validSignedFileUrls, validSignedThumbnailUrls],
  );

  const trackContextMenuOpening = useCallback(() => {
    Analytics.track<OpenAnnotationDetailsContextMenuEventProperties>(
      EventType.openAnnotationDetailsContextMenu,
      {
        via: AnnotationViewerVariant,
      },
    );
  }, [AnnotationViewerVariant]);

  const linkElements = useAppSelector(
    selectChildrenDepthFirst(sphereMarkup, isIElementUrlLink),
    isEqual,
  );
  const links = useMemo<UrlLink[] | undefined>(() => {
    if (linkElements.length === 0) return;
    return linkElements.map((el) => ({
      label: el.descr ?? el.name,
      href: el.uri,
    }));
  }, [linkElements]);

  const tags = useMemo(
    () =>
      markup.labels
        ?.filter(isTagReadable)
        .map((l) => ({ id: l.id, label: l.name, labelType: l.labelType })),
    [markup.labels],
  );

  const isFull = AnnotationViewerVariant === "full";

  const onIconClicked = useCallback(() => {
    if (isFull) {
      onAnnotationViewerClosed();
    } else {
      onAnnotationViewerExpanded();
    }
  }, [isFull, onAnnotationViewerClosed, onAnnotationViewerExpanded]);

  const goToAnnotation = useGoToAnnotation();

  const shouldRenderDetails =
    AnnotationViewerVariant !== "hidden" &&
    AnnotationViewerVariant !== "collapsed";

  const finalTitle = externalMarkup
    ? getExternalAnnotationTitle(
        externalMarkup.externalIssueId,
        externalMarkupData,
        externalAnnotationError?.title,
      )
    : markup.name;
  const finalAssignee = useMemo(() => {
    const assignee = externalMarkup
      ? getGenericUserInfo(externalMarkupData?.assigned_to)
      : mainAssignee;
    return assignee
      ? { ...assignee, isTeam: assignee.authorityType === "UserGroupRef" }
      : undefined;
  }, [externalMarkup, externalMarkupData?.assigned_to, mainAssignee]);
  const finalCreatedBy = externalMarkup
    ? getGenericUserInfo(externalMarkupData?.creation_author)
    : createdBy;
  const externalMarkupAnnotationDueDate = externalMarkupData?.due_date
    ? new Date(externalMarkupData.due_date)
    : undefined;
  const sphereDueDate = dueDate ? new Date(dueDate.value) : undefined;
  const finalDueDate = externalMarkup
    ? externalMarkupAnnotationDueDate
    : sphereDueDate;
  const finalDescription = externalMarkup
    ? getExternalAnnotationDescription(
        externalMarkupData,
        integrationType,
        externalAnnotationError?.message,
      )
    : markup.descr;
  const finalStatus = externalMarkup
    ? externalMarkupData?.topic_status?.name
    : getAnnotationStatusDisplayName(state?.value);
  const finalHasAttachment = externalMarkup ? false : hasAttachment;
  const finalAttachments = externalMarkup ? undefined : attachments;
  const finalLinks = externalMarkup ? externalMarkupLinks : links;
  const finalTags = externalMarkup
    ? externalMarkupData?.labels.map((l) => ({ id: l.id, label: l.name }))
    : tags;
  const finalIsMeasurement = externalMarkup ? false : isMeasurement;

  const markupWorldPosition = useAppSelector(
    selectIElementWorldPosition(markup.id),
  );

  const markupParentId = externalMarkup
    ? externalMarkup.parentId
    : sphereMarkup?.parentId;
  const markupParentElement = useAppSelector(selectIElement(markupParentId));

  const [sparseCoordinateString, setSparseCoordinateString] =
    useState<string>();

  const distanceUnit = useDistanceUnit();
  const markupCoordinateString = useMemo(() => {
    const coordStr = getCoordinateStringZUp(
      markupWorldPosition,
      distanceUnit,
      false,
    );
    setSparseCoordinateString(
      formatSparseCoordinateString(markupWorldPosition, distanceUnit),
    );
    return coordStr;
  }, [distanceUnit, markupWorldPosition]);

  const copyMarkupCoordinates = useCallback((): void => {
    Analytics.track(EventType.copyAnnotationCoordinate);
    navigator.clipboard.writeText(markupCoordinateString);
    openToast({ title: "Coordinates copied to clipboard" });
  }, [markupCoordinateString, openToast]);

  // The signed URLs need to be kept alive, as long as the annotation is visible.
  useKeepSignedUrlsAlive(allSignedUrls, shouldRenderDetails || isEditing);

  const backgroundTask = useAppSelector(
    selectBackgroundTaskForIElement(markup.id),
  );

  const isDeleted =
    backgroundTask?.type === "Mutation" &&
    backgroundTask.mutationType === MutationTaskType.DeleteAnnotation;

  const isAligned = useAppSelector(selectIsElementAligned(markupParentElement));

  const showMarkupCoordinates = useMemo(
    () => isAligned && markupParentElement?.type !== IElementType.model3d,
    [markupParentElement, isAligned],
  );

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "top",
          width: "max-content",
          ...sx,
        }}
      >
        {icon && (
          <Box
            component="div"
            sx={{
              width: iconSize,
              height: iconSize,
              cursor: "pointer",
            }}
            onClick={onIconClicked}
          >
            {icon}
          </Box>
        )}
        {shouldRenderDetails && (
          <AnnotationViewer
            onDescriptionError={(error) =>
              handleErrorWithToast({ title: "Error in description", error })
            }
            onAnnotationBoxClick={onAnnotationViewerClicked}
            onShowMoreButtonClick={() => {
              Analytics.track(EventType.expandAnnotationDetails);
              onAnnotationViewerExpanded();
            }}
            onCloseButtonClick={() => {
              Analytics.track(EventType.closeAnnotationDetails);
              onAnnotationViewerClosed();
            }}
            onGoToButtonClick={() => {
              Analytics.track<GotoAnnotationProperties>(
                EventType.gotoAnnotation,
                { via: "3d scene" },
              );
              goToAnnotation(markup);
            }}
            onShareButtonClick={() => {
              Analytics.track<CreateAnnotationShareLinkEventProperties>(
                EventType.createAnnotationShareLink,
                {
                  via: AnnotationViewerVariant,
                },
              );

              shareAnnotation();
            }}
            onAttachmentOpened={(fileType: string) =>
              Analytics.track<OpenAnnotationAttachmentEventProperties>(
                EventType.openAnnotationAttachment,
                {
                  fileType,
                },
              )
            }
            // External markups are not supported yet
            canEdit={canWriteAnnotations && !externalMarkup && !isDeleted}
            canDelete={canWriteAnnotations && !isDeleted}
            onContextMenuOpen={trackContextMenuOpening}
            onEdit={() => {
              Analytics.track<EditAnnotationEventProperties>(
                EventType.editAnnotation,
                {
                  via: "3d scene",
                },
              );

              setIsEditing(true);
            }}
            markupCoordinateString={
              showMarkupCoordinates ? sparseCoordinateString : undefined
            }
            onCopyMarkupCoordinates={copyMarkupCoordinates}
            onDelete={onDelete}
            variant={AnnotationViewerVariant}
            title={finalTitle}
            assignee={finalAssignee}
            createdBy={finalCreatedBy}
            dueDate={finalDueDate}
            description={finalDescription}
            status={finalStatus}
            externalAnnotationViewerProps={
              externalMarkup && integrationType
                ? {
                    annotationTypeName:
                      getExternalAnnotationTypeName(integrationType),
                    providerName:
                      getExternalAnnotationProviderName(integrationType),
                    link: externalMarkupData
                      ? externalMarkupData.origin_user_interface_url
                      : undefined,
                    logo: (
                      <ExternalAnnotationProviderLogo
                        annotationType={integrationType}
                        sx={{
                          color: grey[0],
                        }}
                      />
                    ),
                    fields: [
                      ...(externalAnnotationError === undefined &&
                      externalMarkupData
                        ? getExternalAnnotationTypeFields(
                            integrationType,
                            externalMarkupData,
                          )
                        : []),
                    ],
                    error: externalAnnotationError,
                  }
                : undefined
            }
            hasAttachment={finalHasAttachment}
            isMeasurement={finalIsMeasurement}
            onCopyToClipboard={onCopyToClipboard}
            files={finalAttachments}
            links={finalLinks}
            tags={finalTags}
            size={annotationSize}
            isLoading={!!backgroundTask}
          >
            {measurement && (
              <MeasurementValuesField measurement={measurement} />
            )}
            {integrationType &&
              externalAnnotationError?.shouldShowProviderAuthorization && (
                <ProviderAuthorization
                  integrationType={integrationType}
                  onAuthorizationButtonClicked={() =>
                    Analytics.track<AuthorizeExternalAnnotationProperties>(
                      EventType.authorizeExternalAnnotation,
                      {
                        via: "callout",
                        service:
                          getExternalAnnotationProviderName(integrationType),
                      },
                    )
                  }
                />
              )}
          </AnnotationViewer>
        )}
      </Stack>
      {isEditing && sphereMarkup !== undefined && (
        <Dialog
          open
          slotProps={{
            paper: {
              sx: {
                padding: 0,
                backgroundColor: "transparent",
                overflow: "hidden",
                boxShadow: "none",
              },
            },
          }}
        >
          <EditAnnotation
            iElement={sphereMarkup}
            title={sphereMarkup.name}
            description={sphereMarkup.descr ?? undefined}
            assignee={assigneeId}
            status={state?.value ?? undefined}
            dueDate={dueDate?.value ? new Date(dueDate.value) : undefined}
            onClose={() => setIsEditing(false)}
          >
            {measurement && (
              <MeasurementValuesField measurement={measurement} />
            )}
          </EditAnnotation>
        </Dialog>
      )}
    </>
  );
}

/**
 * Retrieves the external annotation type fields based on the integration type and external annotation data.
 *
 * @param integrationType The type of BCF services integration.
 * @param externalAnnotationData The external annotation data containing topic information.
 * @returns An array of external annotation fields.
 *
 * If the integration type is not supported, an assertion error is thrown.
 */
function getExternalAnnotationTypeFields(
  integrationType: BcfServicesIntegrationType,
  externalAnnotationData: GetTopicResponse,
): ExternalAnnotationsField[] {
  switch (integrationType) {
    case BcfServicesIntegrationType.autodeskAccIssues:
      return [
        ...(externalAnnotationData.topic_type
          ? [{ name: "Type", value: externalAnnotationData.topic_type.name }]
          : []),
      ];
    case BcfServicesIntegrationType.autodeskAccRfis:
      return [
        ...(externalAnnotationData.priority
          ? [{ name: "Priority", value: externalAnnotationData.priority.name }]
          : []),
      ];
    case BcfServicesIntegrationType.procoreObservations:
      return [
        ...(externalAnnotationData.topic_type
          ? [{ name: "Type", value: externalAnnotationData.topic_type.name }]
          : []),
        ...(externalAnnotationData.priority
          ? [{ name: "Priority", value: externalAnnotationData.priority.name }]
          : []),
      ];
    case BcfServicesIntegrationType.procoreRfis:
      return [
        ...(externalAnnotationData.stage
          ? [{ name: "Stage", value: externalAnnotationData.stage.name }]
          : []),
      ];
    default:
      assert(false, `Integration type: ${integrationType} is not supported`);
  }
}

/**
 * Retrieves the title of the external annotation based on the fetched data.
 *
 * @param id The ID of the external annotation.
 * @param fetchedData The fetched data of the external annotation.
 * @param errorTitle If an error occurred, it will contain the title of the error.
 * @returns The title of the external annotation.
 */
function getExternalAnnotationTitle(
  id: string,
  fetchedData: GetTopicResponse | undefined,
  errorTitle?: string,
): string {
  if (errorTitle) {
    return errorTitle;
  }
  if (!fetchedData) {
    return `Loading annotation ${id}`;
  }
  const isDeleted = GetExternalAnnotationDeleteStatus(fetchedData);
  return isDeleted ? `[DELETED] ${fetchedData.title}` : fetchedData.title;
}

/**
 * Retrieves the description of the external annotation based on the fetched data.
 *
 * @param fetchedData The fetched data of the external annotation.
 * @param integrationType The type of BCF services integration.
 * @param errorMessage If an error occurred, it will contain the details about the error.
 * @returns The description of the external annotation.
 */
function getExternalAnnotationDescription(
  fetchedData: GetTopicResponse | undefined,
  integrationType: BcfServicesIntegrationType | undefined,
  errorMessage: string | undefined,
): string {
  if (errorMessage) {
    return errorMessage;
  }
  return fetchedData && integrationType
    ? removeDeepLinkFromDescription(
        fetchedData.description ?? "",
        getExternalAnnotationProviderName(integrationType),
      )
    : "";
}

/**
 * Checks if the external annotation is marked as deleted.
 *
 * @param annotationData The annotation data to check for deletion status.
 * @returns True if the annotation is marked as deleted, false otherwise.
 */
export function GetExternalAnnotationDeleteStatus(
  annotationData: GetTopicResponse | undefined,
): boolean {
  const deletedField = annotationData?.companion_fields.find(
    (field) => field.id === "deleted",
  );
  return (
    deletedField !== undefined &&
    deletedField.values.length > 0 &&
    deletedField.values[0] === "true"
  );
}
